/**
 * The Google Maps JavaScript loader, shared by the comp-search map (1G).
 *
 * `LocationMap` (review screen) carries its own copy of this and is deliberately left alone. Both
 * use the same <script id>, so whichever mounts first loads the library once and the other reuses
 * it; a second tag for the same library logs a console warning and can re-initialize a live map.
 *
 * The key is the HTTP-referrer-restricted BROWSER key served by `/api/next/map/browser-key`
 * (server env `VALUBRIEF_MAPS_BROWSER_KEY`). It is public by construction; see
 * `server/providers/maps.py::browser_key` for why there is no fallback to the server key.
 */
export const GMAPS_SCRIPT_ID = "vb-gmaps-loader";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GoogleNs = any;

export function loadGoogleMaps(key: string): Promise<GoogleNs> {
  return new Promise((resolve, reject) => {
    const w = window as unknown as { google?: { maps?: unknown } };
    if (w.google?.maps) return resolve(w.google);
    const done = () => resolve((window as unknown as { google: GoogleNs }).google);
    const fail = () => reject(new Error("maps failed to load"));
    const existing = document.getElementById(GMAPS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", done);
      existing.addEventListener("error", fail);
      return;
    }
    const s = document.createElement("script");
    s.id = GMAPS_SCRIPT_ID;
    s.async = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}`;
    s.onload = done;
    s.onerror = fail;
    document.head.appendChild(s);
  });
}
